import { supabase } from "@/integrations/supabase/client";

export interface BulkLetterHeaderInfo {
  fullName: string;
  phoneNumber: string;
  email: string;
  cityST: string;
  linkedin: string;
  companyName: string;
  recipientName: string;
  jobTitle: string;
}

export interface BulkLetterData {
  headerInfo: BulkLetterHeaderInfo;
  coverLetterBody?: string;
  thankYouLetterBody?: string;
  outreachLetterBody?: string;
  resumeIdentifier?: string;
}

export interface BulkLetterRequest {
  letters: BulkLetterData[];
  letterTypes: {
    coverLetter: boolean;
    thankYouLetter: boolean;
    outreachLetter: boolean;
  };
  templateId?: 'template1' | 'template2';
}

interface BulkLetterResponse {
  success: boolean;
  files: {
    filename: string;
    data: string; // base64 encoded
    size: number;
  }[];
  totalFiles: number;
}

export interface StoredBulkLetterFile {
  filename: string;
  fileUrl: string;
  size: number;
  documentType: string;
}

interface StoredBulkLetterResponse {
  success: boolean;
  files: StoredBulkLetterFile[];
  totalFiles: number;
}

export class BulkLetterService {
  static async generateBulkLetters(data: BulkLetterRequest): Promise<BulkLetterResponse> {
    try {
      console.log('Generating bulk letters:', {
        letterCount: data.letters.length,
        letterTypes: data.letterTypes,
        totalLetters: data.letters.length * Object.values(data.letterTypes).filter(Boolean).length
      });

      const { data: response, error } = await supabase.functions.invoke('generate-bulk-letters', {
        body: data
      });

      if (error) {
        console.error('Error calling generate-bulk-letters function:', error);
        throw new Error(`Bulk letter generation failed: ${error.message}`);
      }

      if (!response || !response.success) {
        throw new Error('Invalid response from bulk letter generation');
      }

      console.log('Bulk generation successful:', response);
      return response as BulkLetterResponse;

    } catch (error) {
      console.error('Error in BulkLetterService.generateBulkLetters:', error);
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to generate bulk letters'
      );
    }
  }

  static downloadFileFromBase64(filename: string, base64Data: string): void {
    try {
      // Decode base64 to bytes
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      // Create blob and download
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      
      this.downloadBlob(blob, filename);
    } catch (error) {
      console.error('Error downloading file:', filename, error);
      throw new Error(`Failed to download ${filename}`);
    }
  }

  static async generateAndStoreBulkLetters(data: BulkLetterRequest): Promise<StoredBulkLetterResponse> {
    try {
      console.log('Generating and storing bulk letters:', {
        letterCount: data.letters.length,
        letterTypes: data.letterTypes,
        totalLetters: data.letters.length * Object.values(data.letterTypes).filter(Boolean).length
      });

      // First generate the letters
      const response = await this.generateBulkLetters(data);
      
      // Then store each file in Supabase storage
      const storedFiles: StoredBulkLetterFile[] = [];
      
      for (const file of response.files) {
        try {
          // Convert base64 to blob
          const byteCharacters = atob(file.data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          });

          // Upload to Supabase storage
          const timestamp = Date.now();
          const storagePath = `bulk_${timestamp}_${file.filename}`;
          
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('word-documents')
            .upload(storagePath, blob, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          // Get public URL
          const { data: urlData } = supabase.storage
            .from('word-documents')
            .getPublicUrl(storagePath);

          if (urlData?.publicUrl) {
            // Determine document type from filename
            let documentType = 'letter';
            if (file.filename.toLowerCase().includes('cover')) documentType = 'cover_letter';
            else if (file.filename.toLowerCase().includes('thank')) documentType = 'thank_you_letter';
            else if (file.filename.toLowerCase().includes('outreach')) documentType = 'outreach_letter';

            storedFiles.push({
              filename: file.filename,
              fileUrl: urlData.publicUrl,
              size: file.size,
              documentType
            });
          }
        } catch (error) {
          console.error(`Failed to store file ${file.filename}:`, error);
          // Continue with other files even if one fails
        }
      }

      console.log('Bulk letters stored successfully:', storedFiles);
      return {
        success: true,
        files: storedFiles,
        totalFiles: storedFiles.length
      };

    } catch (error) {
      console.error('Error in BulkLetterService.generateAndStoreBulkLetters:', error);
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to generate and store bulk letters'
      );
    }
  }

  static downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  static async downloadAsZip(
    files: { filename: string; data: string }[],
    zipFilename: string = 'Bulk Letters.zip',
    resumeFiles?: { resumeIdentifier: string; files: File[]; jobTitle: string }[],
    folderNameOverrides?: Record<string, string>,
    rootFiles?: File[]
  ): Promise<void> {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    // Sanitize folder names to avoid creating unintended subfolders from "/" or "\" in job titles
    const sanitizeFolder = (name: string) => name.replace(/[\/\\]/g, '-').trim();
    // Use " - " (space-hyphen-space) as the separator so hyphenated last names like "Curry-Williams" aren't split
    const filenameRegex = /^(.+?)\s-\s(.+?)\s+(Cover Letter|Thank You Letter|Outreach Letter)\s+\d{4}\.docx$/i;

    for (const file of files) {
      const match = file.filename.match(filenameRegex);

      let folderName: string;
      if (match) {
        const jobTitle = match[2].trim();
        folderName = sanitizeFolder(folderNameOverrides?.[jobTitle] || jobTitle);
      } else {
        folderName = 'Other';
      }

      const byteCharacters = atob(file.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Sanitize the filename portion too so any "/" inside the job title doesn't create extra folders
      const safeFilename = file.filename.replace(/[\/\\]/g, '-');
      zip.file(`${folderName}/${safeFilename}`, byteArray);
    }

    // Add resume files to their corresponding job title folders
    if (resumeFiles && resumeFiles.length > 0) {
      const firstMatch = files[0]?.filename.match(/^(.+?)\s-\s/);
      const clientName = firstMatch ? firstMatch[1].trim() : 'Resume';

      for (const resume of resumeFiles) {
        const folderName = sanitizeFolder(resume.jobTitle || 'Other');
        for (const resumeFile of resume.files) {
          const ext = resumeFile.name.split('.').pop() || 'pdf';
          const versionMatch = resumeFile.name.replace(/\.[^.]+$/, '').match(/[\s_\-\(]*(v\d+)[\)]*$/i);
          const versionSuffix = versionMatch ? ` ${versionMatch[1].toUpperCase()}` : '';
          const resumeFilename = `${clientName} - ${folderName} Resume 2026${versionSuffix}.${ext}`;
          const arrayBuffer = await resumeFile.arrayBuffer();
          zip.file(`${folderName}/${resumeFilename}`, new Uint8Array(arrayBuffer));
        }
      }
    }

    // Add root-level files (LinkedIn profiles, executive bios, etc.) — no subfolder
    if (rootFiles && rootFiles.length > 0) {
      for (const rootFile of rootFiles) {
        const arrayBuffer = await rootFile.arrayBuffer();
        zip.file(rootFile.name, new Uint8Array(arrayBuffer));
      }
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    this.downloadBlob(zipBlob, zipFilename);
  }
}